import { useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { Camera, Clock, MapPin } from 'lucide-react';
import { Tag, cn } from '../ui';
import { request } from '../api/client';
import { formatIstTime } from '../lib/time';
import { inr2 } from '../lib/format';
import { OrderLink } from './EntityLinks';
import { AttachmentViewer } from './AttachmentPreview';

/**
 * EVERYTHING A VISIT LEFT BEHIND, READ THE SAME WAY WHEREVER IT IS OPENED.
 *
 * The rep's own day (My Day → Today's visits) and the reviewer's record (Attendance → a day → Field
 * work) are two views of the same visit. They used to disagree: My Day expanded to the timings,
 * feedback and photos, while the attendance record — the screen where somebody decides whether the
 * day can be trusted — showed only the name. The evidence a reviewer needs most (was the photo taken
 * at the site? how long did the journey take?) was the evidence they could not see.
 *
 * Photos are fetched from the visit detail only when this renders, i.e. when a row is expanded, so a
 * day with a dozen visits costs nothing until someone opens one.
 */

/** tinyint columns arrive as 0/1, not booleans — `=== false` never matches a 0. */
const isFalse = (v: unknown) => v === false || v === 0 || v === '0';
const isTrue = (v: unknown) => v === true || v === 1 || v === '1';

const dur = (min?: number | null) => (min == null ? null : min >= 60 ? `${Math.floor(min / 60)}h ${min % 60}m` : `${min}m`);

type PhotoVerdict = 'at site' | 'off-site' | 'no location';

/**
 * Where a photo was taken, judged from the photo's own GPS. A photo with NO location is reported as
 * such even though the server marks it inside the geofence — "in" by default is not evidence.
 */
export function photoVerdict(p: any): PhotoVerdict {
  if (p?.gpsLat == null || p.gpsLat === '') return 'no location';
  return isFalse(p.withinGeofence) ? 'off-site' : 'at site';
}

const VERDICT_TONE: Record<PhotoVerdict, 'green' | 'gold' | 'neutral'> = {
  'at site': 'green', 'off-site': 'gold', 'no location': 'neutral',
};

const SENTIMENT: Record<string, { tone: 'green' | 'red' | 'gold'; label: string }> = {
  POSITIVE: { tone: 'green', label: '🙂 Positive' },
  NEGATIVE: { tone: 'red', label: '🙁 Negative' },
  NEUTRAL: { tone: 'gold', label: '😐 Neutral' },
};

/** Was the rep inside the customer's geofence when they marked Reached? */
export const visitOffSite = (v: any) => isFalse(v?.checkInWithinGeofence);

export function VisitEvidence({ visit: v, showOrder = true }: {
  visit: any;
  /** Off where the row above already shows the order (the attendance record puts it on the row). */
  showOrder?: boolean;
}) {
  const { data: detail, isLoading, isError } = useQuery({
    queryKey: ['visit-detail', String(v.id)],
    queryFn: () => request<any>({ url: `/visits/${v.id}` }),
  });
  const [viewing, setViewing] = useState<number | null>(null);

  const photos: any[] = detail?.photos ?? [];
  const offSite = visitOffSite(v);
  const travelMin = v.startedAt && v.checkInAt
    ? Math.max(0, Math.round((new Date(v.checkInAt).getTime() - new Date(v.startedAt).getTime()) / 60000))
    : null;
  const atSite = dur(v.durationMin);
  const sent = v.feedbackSentiment ? SENTIMENT[String(v.feedbackSentiment)] : undefined;
  const verdicts = photos.map(photoVerdict);
  const counts = (['at site', 'off-site', 'no location'] as PhotoVerdict[])
    .map((k) => [k, verdicts.filter((x) => x === k).length] as const)
    .filter(([, n]) => n > 0);
  const photoCount = photos.length || Number(v.photosCount ?? 0);
  const labels = photos.map((p, i) => `Photo ${i + 1} · ${formatIstTime(p.takenAt)} · ${verdicts[i]}`);

  return (
    <div className="space-y-3 type-body">
      {/* The visit as a timeline: when they set out, arrived and left, and whether arrival was on site. */}
      <dl className="grid grid-cols-2 gap-px overflow-hidden rounded-card border border-line bg-line sm:grid-cols-4">
        <Fact label="Departed" value={v.startedAt ? formatIstTime(v.startedAt) : '—'} />
        <Fact
          label="Reached"
          value={v.checkInAt ? formatIstTime(v.checkInAt) : 'Not yet'}
          note={travelMin != null ? `${travelMin} min travel` : undefined}
        />
        <Fact
          label="Left"
          value={v.checkOutAt ? formatIstTime(v.checkOutAt) : v.checkInAt ? 'Still at site' : '—'}
          note={v.checkOutAt && atSite ? `${atSite} at site` : undefined}
        />
        <Fact
          label="Geofence"
          value={v.checkInAt ? <Tag tone={offSite ? 'gold' : 'green'}><MapPin className="size-3" />{offSite ? 'Outside' : 'Inside'}</Tag> : '—'}
          note={v.checkInAccuracyM != null ? `±${Math.round(Number(v.checkInAccuracyM))} m` : undefined}
        />
      </dl>

      {(sent || v.feedbackRemark) && (
        <Line label="Feedback">
          {sent && <Tag tone={sent.tone}>{sent.label}</Tag>}
          {v.feedbackRemark && <span className="text-content">{v.feedbackRemark}</span>}
          {isTrue(v.followUpNeeded) && v.followUpDate && (
            <span className="text-subtle">· follow-up {dayjs(v.followUpDate).format('DD MMM YYYY')}</span>
          )}
        </Line>
      )}

      {showOrder && (isTrue(v.orderPlaced) || v.order) && (
        <Line label="Order">
          {v.order?.orderNo ? <OrderLink id={v.order.id ?? v.orderId}>{v.order.orderNo}</OrderLink> : <span>placed</span>}
          {v.order?.grandTotal != null && <span className="tabular-nums text-subtle">· {inr2(v.order.grandTotal)}</span>}
        </Line>
      )}

      {v.notes && <Line label="Notes"><span className="text-content">{v.notes}</span></Line>}

      <div>
        <div className="mb-1.5 flex flex-wrap items-center gap-1.5">
          <span className="inline-flex items-center gap-1 type-label text-subtle"><Camera className="size-3.5" />Photos ({photoCount})</span>
          {/* The verdict counts come first: "3 no location" is the fact a reviewer is scanning for. */}
          {counts.map(([k, n]) => <Tag key={k} tone={VERDICT_TONE[k]}>{n} {k}</Tag>)}
        </div>
        {photos.length ? (
          <div className="flex flex-wrap gap-2.5">
            {photos.map((p, i) => {
              const verdict = verdicts[i];
              return (
                <button
                  key={p.id ?? i}
                  type="button"
                  onClick={() => setViewing(i)}
                  title={labels[i]}
                  className="group w-20 text-left"
                >
                  <img
                    src={p.url}
                    alt={labels[i]}
                    loading="lazy"
                    className={cn(
                      'block size-20 rounded-control object-cover transition-opacity group-hover:opacity-85',
                      verdict === 'off-site' ? 'ring-2 ring-warning-500' : 'ring-1 ring-line',
                    )}
                  />
                  <span className="mt-1 flex items-center gap-1 type-caption text-subtle"><Clock className="size-3" />{formatIstTime(p.takenAt)}</span>
                  <span className={cn('flex items-center gap-1 type-caption',
                    verdict === 'at site' ? 'text-brand-700 dark:text-brand-400'
                      : verdict === 'off-site' ? 'text-warning-600 dark:text-warning-500' : 'text-subtle')}>
                    <MapPin className="size-3" />{verdict}
                  </span>
                </button>
              );
            })}
          </div>
        ) : (
          <span className="type-caption text-subtle">
            {photoCount > 0 ? (isError ? 'Photos could not be loaded.' : isLoading ? 'Loading photos…' : 'None') : 'No photos taken.'}
          </span>
        )}
      </div>

      {viewing != null && (
        <AttachmentViewer
          files={photos.map((p) => p.url)}
          labels={labels}
          index={viewing}
          label="Visit photo"
          onIndex={setViewing}
          onClose={() => setViewing(null)}
        />
      )}
    </div>
  );
}

function Fact({ label, value, note }: { label: string; value: ReactNode; note?: string }) {
  return (
    <div className="bg-surface px-3 py-2">
      <dt className="type-caption text-subtle">{label}</dt>
      <dd className="mt-0.5 type-body-medium tabular-nums text-content">{value}</dd>
      {note && <dd className="type-caption text-subtle">{note}</dd>}
    </div>
  );
}

function Line({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
      <span className="type-label text-subtle">{label}</span>
      {children}
    </div>
  );
}
